import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { PlanogramProduct } from '../models/layout/planogram-product';
import { PlanogramShelf } from '../models/layout/planogram-shelf';

export interface PlaceholderState {
  type: 'stack' | 'product' | 'shelf' | 'floating-shelf' | null;
  dropIndex: number | null;
  shelfIndex: number | null;
  stackIndex: number | null; // Only for product placeholders
  position: number | null;
  product: PlanogramProduct | null;
  shelf: PlanogramShelf | null;
}

@Injectable({ providedIn: 'root' })
export class PlaceholderService {
  private placeholderSubject = new BehaviorSubject<PlaceholderState>({
    type: null,
    dropIndex: null,
    shelfIndex: null,
    stackIndex: null,
    position: -1,
    product: null,
    shelf: null
  });

  placeholder$ = this.placeholderSubject.asObservable();

  setShelfPlaceholder(dropIndex: number, shelfIndex: number, shelf: PlanogramShelf) {
    this.placeholderSubject.next({
      type: 'shelf',
      dropIndex,
      shelfIndex,
      stackIndex: null,
      position: -1,
      product: null,
      shelf
    });
  }

  setStackPlaceholder(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    product: PlanogramProduct
  ) {
    this.placeholderSubject.next({
      type: 'stack',
      dropIndex,
      shelfIndex,
      stackIndex,
      position: 0,
      product,
      shelf: null
    });
  }

  setProductPlaceholder(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    position: number,
    product: PlanogramProduct
  ) {
    this.placeholderSubject.next({
      type: 'product',
      dropIndex,
      shelfIndex,
      stackIndex,
      position,
      product,
      shelf: null
    });
  }

  /** Marks the floating shelf as the current drop target for a dragged product. */
  setFloatingShelfPlaceholder(product: PlanogramProduct) {
    this.placeholderSubject.next({
      type: 'floating-shelf',
      dropIndex: null,
      shelfIndex: null,
      stackIndex: null,
      position: -1,
      product,
      shelf: null
    });
  }

  clearPlaceholder() {
    this.placeholderSubject.next({
      type: null,
      dropIndex: null,
      shelfIndex: null,
      stackIndex: null,
      position: -1,
      product: null,
      shelf: null
    });
  }

  getCurrentPlaceholder(): PlaceholderState {
    return this.placeholderSubject.value;
  }

  // Helper methods to check if placeholder should show at specific location
  shouldShowShelfPlaceholder(dropIndex: number, shelfIndex: number): boolean {
    const current = this.getCurrentPlaceholder();
    return (
      current.type === 'shelf' &&
      current.dropIndex === dropIndex &&
      current.shelfIndex === shelfIndex
    );
  }

  shouldShowStackPlaceholder(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number
  ): boolean {
    const current = this.getCurrentPlaceholder();
    return (
      current.type === 'stack' &&
      current.dropIndex === dropIndex &&
      current.shelfIndex === shelfIndex &&
      current.stackIndex === stackIndex
    );
  }

  shouldShowProductPlaceholder(
    dropIndex: number,
    shelfIndex: number,
    stackIndex: number,
    position: number
  ): boolean {
    const current = this.getCurrentPlaceholder();
    return (
      current.type === 'product' &&
      current.dropIndex === dropIndex &&
      current.shelfIndex === shelfIndex &&
      current.stackIndex === stackIndex &&
      current.position === position
    );
  }
}
